import * as React from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Backdrop, Button, Grid, Typography } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { findSymbol, getSymbol } from "../utils";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../utils";

export default function AppMenuItem({
  items,
  onItemSelect,
  defaultValue,
  labelKey,
  identifierKey = "id",
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const dashboardData = useSelector(({ dashboard }) => dashboard);
  const symbols = useSelector(getSymbol);
  const { t } = useTranslation();
  const open = Boolean(anchorEl);
  const menuRef = React.useRef(null);
  const [filteredList, setFilteredList] = React.useState([])
  const handleClick = (event) => {
    if (items?.length > 1)
      setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleSelect = (item) => {
    handleClose();
    onItemSelect(item);
  };
  React.useEffect(() => {
    const list = document.getElementById('app-date-list');
    if (list) {
      open ? list.style.overflow = 'hidden' : list.style.overflow = "";;
    }
    if (open) {
      setTimeout(() => {
        if (menuRef.current) {
          menuRef.current.scrollTop = 0;
        }
      }, 50);
    }

  }, [open]);

  React.useEffect(() => {
    const rearrangedItems = [...items]; // Clone the array to avoid mutations
    const matchingIndex = rearrangedItems.findIndex(
      (v) => v?.payoutAccount?.id === defaultValue?.payoutAccount?.id
    );

    if (matchingIndex > -1) {
      const [matchingItem] = rearrangedItems.splice(matchingIndex, 1); // Remove the matching item
      rearrangedItems.unshift(matchingItem);
    }
    setFilteredList(rearrangedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, defaultValue])
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", textAlign: "center" }}>
        <Button
          onClick={handleClick}
          size="small"
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            padding: "15px 10px",
            textTransform: "unset",
            color: '#F8F8F8',
            backgroundColor: "rgba(25, 118, 210, 0.08)",
            borderRadius: 2,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <Grid container>
            <Grid item xs={12} md={8}>
              {defaultValue?.payoutAccount?.accountRef && <Typography
                fontWeight={500}
                fontSize={14}
                textAlign={"start"}
                textTransform={"capitalize"}
                letterSpacing={0.8}
              >
                {defaultValue?.merchant?.companyName ||
                  defaultValue?.merchant?.username} ({defaultValue?.payoutAccount?.accountRef?.slice(-5)})
              </Typography>}
              {!defaultValue?.payoutAccount?.accountRef && <Typography
                fontWeight={500}
                fontSize={20}
                textAlign={"start"}
                textTransform={"capitalize"}
                letterSpacing={0.8}
              >
                {t('balance')}
              </Typography>}
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box display={'flex'} flexDirection={'column'} justifyContent={'flex-end'} alignItems={'flex-end'}>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"flex-start"}
                >
                  <Typography variant="subtitle1" fontSize={22} fontWeight={500}>
                    {findSymbol(symbols, defaultValue)}&nbsp;{formatNumber(defaultValue?.totalBalance)}
                  </Typography>
                  {items?.length > 1 && <Box display={'flex'} alignItems={'center'}>{!open && <KeyboardArrowDown sx={{ color: "#F8F8F8" }} />}
                    {open && <KeyboardArrowUp sx={{ color: "#F8F8F8" }} />}
                  </Box>}
                </Box>
                {!!dashboardData?.balanceOnHold && <Box marginTop={-1} marginRight={items?.length ? 3 : 0}>
                  <Typography fontSize={13} color={'#D4D4D8'}>{t('availableBalance')}</Typography>
                </Box>}
              </Box>

            </Grid>
          </Grid>
        </Button>
      </Box>
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={open}
        onClick={handleClose}
      >
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          slotProps={{
            paper: {
              ref: menuRef,
              elevation: 0,
              sx: {
                overflowY: "auto",
                maxHeight: {
                  xs: 350,
                  md: 400
                },
                borderRadius: 2,
                backgroundColor: "transparent",
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            },
          }}
          transformOrigin={{ horizontal: "center", vertical: "top" }}
          anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        >
          {filteredList?.map((item, index) => (
            <MenuItem
              key={index}
              aria-label="menuitem"
              sx={{ padding: 2, borderRadius: 2, margin: 0.5 }}
              onClick={() => handleSelect(item)}
              selected={defaultValue?.[identifierKey] === item?.[identifierKey]}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: "#27567c",
                  fontSize: 16,
                  textTransform: "capitalize",
                }}
              >
                {item?.merchant?.companyName?.[0] ||
                  item?.merchant?.username?.[0]}
              </Avatar>
              <Box sx={{ width: 500 }}>
                <Typography
                  fontWeight={700}
                  fontSize={16}
                  textAlign={"start"}
                  textTransform={"capitalize"}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "220px",
                  }}
                >
                  {item?.merchant?.companyName || item?.merchant?.username}
                </Typography>
                <Typography
                  color="#DDDADA"
                  display={"block"}
                  fontSize={12}
                  textAlign={"start"}
                >{`Account# ${item?.payoutAccount?.accountRef}`}</Typography>
              </Box>
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"flex-end"}
                flexGrow={1}
              >
                <Typography
                  variant="subtitle1"
                  textAlign={"right"}
                  fontSize={16}
                >
                   {findSymbol(symbols, item)} {formatNumber(item?.totalBalance)}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </Backdrop>
    </Box>
  );
}
